import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  FormHelperText,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import GroupIcon from "@mui/icons-material/Group";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";

import { RootState } from "../../store/Store";
import {
  fetchData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import PageTitle from "../../components/Page Component/PageTitle";
import { setIsLoading } from "../../store/LoadingSlice";
import { userComplianceAssignmentsUrl } from "../../Service/Apis";
import { titleCase, formatDateToDDMMYYYY, getComplianceStatusColor } from "./utils";
import { getDataGridPaginationProps } from "utils/paginationConfig";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import { formatDateConsistent } from "../../utils/dateUtils";

interface ComplianceAssignment {
  assignment_id: string;
  project_name: string;
  template_name: string;
  assigned_to_name: string;
  due_date: string;
  due_date_str: string;
  status: "pending" | "in_progress" | "completed" | "overdue";
  month_year_str: string;
  compliance_rate: number;
  is_viewonly?: boolean;
  compliance_score?: {
    overall_score: number;
    category_scores: Record<string, any>;
    total_yes: number;
    total_no: number;
    total_answered: number;
  };
}

const ManagerCompliance: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<ComplianceAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<
    ComplianceAssignment[]
  >([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [monthYearFilter, setMonthYearFilter] = useState<string>("");
  const [accessTypeFilter, setAccessTypeFilter] = useState<string>("");
  const [availableMonthYears, setAvailableMonthYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    fetchAssignments();
  }, []);

  // Helper function to determine access type for filtering
  const getAccessType = (assignment: ComplianceAssignment): string => {
    const isViewOnly = assignment.is_viewonly;
    const status = assignment.status;
    const hasProject = assignment.project_name && assignment.project_name.trim() !== "";
    const currentRole = selectedRole || "Employee";
    
    if (status === "completed") {
      return "view_only";
    } else if  (currentRole === "Employee" && hasProject) {
      return "role_required_pco";
    } else if (currentRole === "Process Compliance Officer" && !hasProject) {
      return "role_required_employee";
    } else if (isViewOnly) {
      return "team_submission";
    } else {
      return "editable";
    }
  };

  useEffect(() => {
    // Apply filters
    let filtered = [...assignments];

    if (statusFilter) {
      filtered = filtered.filter(
        (assignment) => assignment.status === statusFilter
      );
    }

    if (monthYearFilter) {
      filtered = filtered.filter(
        (assignment) => assignment.month_year_str === monthYearFilter
      );
    }

    if (accessTypeFilter) {
      filtered = filtered.filter(
        (assignment) => getAccessType(assignment) === accessTypeFilter
      );
    }

    setFilteredAssignments(filtered);
  }, [statusFilter, monthYearFilter, accessTypeFilter, assignments, selectedRole]);

  const fetchAssignments = async () => {
    try {
      dispatch(setIsLoading(true));
      setLoading(true);

      const response = await dispatch2(
        fetchData(`${userComplianceAssignmentsUrl}?role=${selectedRole}`)
      );

      const assignmentData = response as ComplianceAssignment[];
      setAssignments(assignmentData);
      setFilteredAssignments(assignmentData);

      // Extract unique month/years for filter
      const uniqueMonthYears = [
        ...new Set(assignmentData.map((a) => a.month_year_str)),
      ];
      setAvailableMonthYears(uniqueMonthYears);

      if (assignmentData.length === 0) {
        showToast("No compliance assignments found", "info", true);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      showToast("Error fetching your compliance assignments", "error", true);
    } finally {
      dispatch(setIsLoading(false));
      setLoading(false);
    }
  };

  const navigateToForm = (assignmentId: string) => {
    navigate(`/process-compliance-form/${assignmentId}`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <AssignmentTurnedInIcon />;
      case "in_progress":
        return <AssignmentIcon />;
      case "pending":
        return <PendingActionsIcon />;
      case "overdue":
        return <AssignmentLateIcon />;
      default:
        return <AssignmentIcon />;
    }
  };

  // DataGrid column definitions for the assignments table
  const complianceColumns: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr No.",
      width: 80,
      sortable: false,
      valueGetter: (params) =>
        filteredAssignments.findIndex(
          (a) => a.assignment_id === params.row.assignment_id
        ) + 1,
      renderHeader: () => <strong>Sr No.</strong>,
    },
    {
      field: "project_name",
      headerName: "Project",
      flex: 1,
      minWidth: 220,
      renderHeader: () => <strong>Project</strong>,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "template_name",
      headerName: "Template",
      width: 200,
      renderHeader: () => <strong>Template</strong>,
    },
    {
      field: "month_year_str",
      headerName: "Period",
      width: 120,
      renderHeader: () => <strong>Period</strong>,
    },
    {
      field: "due_date",
      headerName: "Due Date",
      width: 130,
      valueFormatter: (params) => formatDateToDDMMYYYY(params.value),
      renderHeader: () => <strong>Due Date</strong>,
    },
    {
      field: "access_type",
      headerName: "Access Type",
      width: 250,
      sortable: false,
      renderHeader: () => <strong>Access Type</strong>,
      renderCell: (params) => {
        const assignment = params.row;
        const isViewOnly = assignment.is_viewonly;
        const status = assignment.status;
        const hasProject = assignment.project_name && assignment.project_name.trim() !== "";
        
        // Get selected role from localStorage
        const currentRole = selectedRole || "Employee";
        
        let label = "";
        let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
        let variant: "filled" | "outlined" = "filled";
        
        // Status-based check (highest priority)
        if (status === "completed") {
          // Completed assignments are always view-only regardless of role
          label = "View Only";
          color = "success";
        } else if (currentRole === "Employee" && hasProject) {
          // Employee viewing project-based assignment
          label = "PCO Role Required";
          color = "warning";
          variant = "outlined";
        } else if (currentRole === "Process Compliance Officer" && !hasProject) {
          // PCO viewing direct assignment (no project)
          label = "Employee Role Required";
          color = "warning";
          variant = "outlined";
        } else if (isViewOnly) {
          // Team submission - other PCO's completed work
          label = "Team Submission";
          color = "info";
          variant = "outlined";
        } else {
          // Own active assignment - editable
          label = "Editable";
          color = "primary";
        }
        
        return (
          <Chip
            label={label}
            size="small"
            color={color}
            variant={variant}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 240,
      renderHeader: () => <strong>Status</strong>,
      renderCell: (params) => (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Chip
            label={titleCase(params.value)}
            size="small"
            sx={{
              backgroundColor: getComplianceStatusColor(params.value),
              color: "white",
            }}
          />
          {params.row.is_viewonly && (
            <Chip label="Team Submission" size="small" color="info" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: "compliance_score",
      headerName: "Score",
      width: 200,
      renderHeader: () => <strong>Score</strong>,
      renderCell: (params) => {
        const cs = params.row.compliance_score;
        if (!cs || cs.total_answered === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              N/A
            </Typography>
          );
        }
        return (
          <Tooltip
            title={`${cs.total_yes} Yes out of ${cs.total_answered} answered (N/A excluded)`}
          >
            <Chip
              label={`${cs.total_yes}/${cs.total_answered} (${cs.overall_score.toFixed(1)}%)`}
              size="small"
              color={
                cs.overall_score >= 80
                  ? "success"
                  : cs.overall_score >= 60
                  ? "warning"
                  : "error"
              }
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderHeader: () => <strong>Actions</strong>,
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.status === "completed" ? "View Form" : "Edit Form"
          }
        >
          <IconButton
            color="primary"
            size="small"
            onClick={() => navigateToForm(params.row.assignment_id)}
          >
            {params.row.status === "completed" ? (
              <VisibilityIcon />
            ) : (
              <EditIcon />
            )}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Calculate counts for summary
  const pendingCount = assignments.filter((a) => a.status === "pending").length;
  const overdueCount = assignments.filter((a) => a.status === "overdue").length;
  const inProgressCount = assignments.filter(
    (a) => a.status === "in_progress"
  ).length;
  const completedCount = assignments.filter(
    (a) => a.status === "completed"
  ).length;

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ mb: 3 }}>
            <PageTitle>My Compliance Assignments</PageTitle>
          </Box>

          {/* Summary cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="black"
                    >
                      Pending
                    </Typography>
                    <PendingActionsIcon color="warning" />
                  </Box>
                  <Typography variant="h4">{pendingCount}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="black"
                    >
                      In Progress
                    </Typography>
                    <AssignmentIcon color="info" />
                  </Box>
                  <Typography variant="h4">{inProgressCount}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="black"
                    >
                      Completed
                    </Typography>
                    <AssignmentTurnedInIcon color="success" />
                  </Box>
                  <Typography variant="h4">{completedCount}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: "100%", bgcolor: "#f9f9f9" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="black"
                    >
                      Overdue
                    </Typography>
                    <AssignmentLateIcon color="error" />
                  </Box>
                  <Typography variant="h4">{overdueCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">
                  Filter by Status
                </InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="month-filter-label">
                  Filter by Period
                </InputLabel>
                <Select
                  labelId="month-filter-label"
                  value={monthYearFilter}
                  label="Filter by Period"
                  onChange={(e) => setMonthYearFilter(e.target.value)}
                >
                  <MenuItem value="">All Periods</MenuItem>
                  {availableMonthYears.map((monthYear) => (
                    <MenuItem key={monthYear} value={monthYear}>
                      {monthYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="access-type-filter-label">
                  Filter by Access Type
                </InputLabel>
                <Select
                  labelId="access-type-filter-label"
                  value={accessTypeFilter}
                  label="Filter by Access Type"
                  onChange={(e) => setAccessTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="editable">Editable</MenuItem>
                  <MenuItem value="view_only">View Only</MenuItem>
                  <MenuItem value="team_submission">Team Submission</MenuItem>
                  <MenuItem value="role_required_pco">PCO Role Required</MenuItem>
                  <MenuItem value="role_required_employee">Employee Role Required</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* Content */}
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}
            >
              <CircularProgress />
            </Box>
          ) : filteredAssignments.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No compliance assignments match your filters. Try changing the
              filters or check back later.
            </Alert>
          ) : (
            <DataGrid
              rows={filteredAssignments}
              columns={complianceColumns}
              getRowId={(row) => row.assignment_id}
              autoHeight
              {...getDataGridPaginationProps()}
              disableRowSelectionOnClick
              slots={{ noRowsOverlay: CustomNoRowsOverlay }}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
                "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#fafafa" },
              }}
            />
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagerCompliance;
